import { useCallback, useRef, useState } from "react";
import { coverTransform } from "../utils/canvasEffects";
import { clearFilterCache, dropPhotoFromCache } from "../utils/filterCache";

// Quản lý các tấm ảnh trên dải: thêm ảnh vào ô kế tiếp, chụp lại tấm cuối, xoá hết.
export function useStrip(selectedFrame) {
    const [photos, setPhotos] = useState([]);

    // Ô kế tiếp sẽ được lấp. Dùng ref chứ không suy ra từ photos.length để hai
    // lần chụp liên tiếp không cùng nhắm vào một ô khi state chưa kịp cập nhật.
    const slotCursorRef = useRef(0);
    const photoIdRef = useRef(0);

    // Bản sao "mới nhất" để các callback bên dưới giữ được tham chiếu ổn định
    // mà vẫn đọc đúng giá trị hiện tại.
    const frameRef = useRef(selectedFrame);
    frameRef.current = selectedFrame;

    const photosRef = useRef(photos);
    photosRef.current = photos;

    // Trả về true nếu tấm này vừa lấp đầy ô cuối cùng.
    const addPhoto = useCallback(img => {
        const frame = frameRef.current;
        if (!frame) return false;

        const stripSize = frame.slots.length;
        const slotIndex = slotCursorRef.current;
        if (slotIndex >= stripSize) return false;
        slotCursorRef.current = slotIndex + 1;

        const { scale, offsetX, offsetY } = coverTransform(img, frame.slots[slotIndex]);

        photoIdRef.current += 1;
        setPhotos(p => [
            ...p,
            { id: photoIdRef.current, img, slotIndex, scale, offsetX, offsetY }
        ]);

        return slotIndex + 1 === stripSize;
    }, []);

    const movePhoto = useCallback((id, patch) => {
        setPhotos(prev => prev.map(p => (p.id === id ? { ...p, ...patch } : p)));
    }, []);

    // Cố ý KHÔNG đặt phần dọn dẹp vào trong hàm cập nhật state: StrictMode gọi
    // hàm đó hai lần, con trỏ ô sẽ bị lùi hai bậc.
    const redoLastPhoto = useCallback(() => {
        const prev = photosRef.current;
        if (!prev.length) return;

        dropPhotoFromCache(prev[prev.length - 1].id);
        slotCursorRef.current = Math.max(0, slotCursorRef.current - 1);
        setPhotos(prev.slice(0, -1));
    }, []);

    const resetStrip = useCallback(() => {
        setPhotos([]);
        slotCursorRef.current = 0;
        clearFilterCache();
    }, []);

    return { photos, addPhoto, movePhoto, redoLastPhoto, resetStrip };
}

export default useStrip;
